/**
 * レポートデータ更新（AnalysisData）の入力・実行前バリデーション（SR-047〜055、§7.4.3 / §7.5.3 / §7.7.3 / §8.1）。
 *
 * SR-056: 本画面ではファイルアップロードの入力チェックを行わない（ファイル管理画面で実施）。当ファイルにアップロード UI 向け検証は置かない。 SR-057:
 * 相関チェック（入力項目間）は本画面では定義なし。当ファイルに相関バリデーションは置かない。
 */

import { getMessage } from '../../../../common/utils/MessageUtils'

import {
  DATA_FIX_STATUS_FIX,
  GENERIC_DATA_KIND_GENERIC_ITEM,
  GENERIC_STATUS_PENDING_VERIFICATION,
  GENERIC_STATUS_VERIFYING,
  GENERIC_STATUS_VERIFICATION_ERROR,
  GENERIC_UPLOAD_FILE_STATUS_PENDING_DELETION,
  GENERIC_UPLOAD_FILE_STATUS_UPLOADED
} from './types'

/** データ更新事前チェック1（SR-051）— 9.2 により getMessage 非使用 */
export const MSG_DATA_UPDATE_REQUIRED_FILES_MISSING = '必須ファイルが未登録のためデータ更新できません。'

/** データ更新事前チェック2（SR-052） */
export const MSG_DATA_UPDATE_VERIFYING_OR_ERROR = '検証中またはエラーのデータ分類があるためデータ更新できません。'

/** データ更新事前チェック3（SR-053）— 当タスク自身が更新中（creating）の場合 */
export const MSG_DATA_UPDATE_CREATING = 'データ更新中です。データ更新完了を待ってから再度実行してください。'

/**
 * 同一法人の他期間タスクが更新中の場合に create_data API（C5 チェック）が返す文言。 文言は
 * `chrofy_api/functions/create_data/create_data_utils/messages.py` と一致させる。 画面ではエラー（赤文字）ではなく注記スタイル（黒文字・※付き）で表示する。
 */
export const MSG_DATA_UPDATE_OTHER_PERIOD_CREATING =
  '他の期間のデータが更新中です。データ更新完了を待ってから再度実行してください。'

/** データ更新事前チェック4（SR-054） */
export const MSG_DATA_UPDATE_ALREADY_FIXED = 'データ確定済のためデータ更新できません。'

/** データ更新ボタン活性条件 — 全ファイルがアップロード済みであること */
export const MSG_DATA_UPDATE_FILES_NOT_ALL_UPLOADED = 'ファイルの処理が完了していないため、データ更新できません。'

/** データ検証一括の実行前（SR-055 / §7.4.3 / §8.3）— 9.2 により getMessage 非使用 */
export const MSG_VALIDATION_BULK_NO_PENDING = '検証待ちのデータ分類がありません。'

/**
 * データ作成処理中（dataTaskStatus = creating）のため、データ検証を実行不可なことを示すバナー文言。 `validationBulkDisabled` 起因の非活性理由をユーザに可視化するために UI
 * 上で表示する（GenericMapping と共通文言）。
 */
export const MSG_VALIDATION_DISABLED_TASK_CREATING = 'データ更新中のため、データ検証を実行できません。'

/** 項目マスタ（IF02090）が取得できず、マッピング設定 Excel を出力できないことを示す文言。 ダウンロードボタンの `title` に設定して非活性理由をユーザに可視化する（実装計画 5.3）。 */
export const MSG_MAPPING_EXPORT_DISABLED = '項目情報を取得できないため、マッピング設定をダウンロードできません。'

/** §8.1 許諾日形式（SR-049）— Message.js に同一文言テンプレートがないためリテラル */
const MSG_PERMIT_DATE_FORMAT = '「許諾日」の形式が正しくありません。'

/** SR-053: データ更新中（data_task_status は表示中タスク自身の作成状態） */
const DATA_TASK_STATUS_CREATING = 'creating'

/**
 * 許諾日が YYYY/MM/DD かつ実在日付かを判定する（§8.1・SR-049）
 *
 * @param {string} value トリム済み想定
 * @returns {boolean}
 */
function _isValidYyyyMmDd(value) {
  if (!/^\d{4}\/\d{2}\/\d{2}$/.test(value)) {
    return false
  }
  const [ys, ms, ds] = value.split('/')
  const y = Number(ys)
  const m = Number(ms)
  const d = Number(ds)
  const dt = new Date(y, m - 1, d)
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
}

/**
 * タスクに紐づく各データ分類について、upload_file_list に同一 data_classification の行が1件以上あるか（SR-051）
 *
 * @param {import('./types').GenericDataKindItem[] | null | undefined} genericDataKinds
 * @param {import('./types').UploadFileListItem[] | null | undefined} uploadFileList
 * @returns {boolean}
 */
function _everyKindHasUploadedFile(genericDataKinds, uploadFileList) {
  const kinds = (genericDataKinds ?? []).filter((k) => k?.genericDataKindCode !== GENERIC_DATA_KIND_GENERIC_ITEM)
  if (kinds.length === 0) {
    return true
  }
  const codesWithUpload = new Set(
    (uploadFileList ?? [])
      .map((row) => (row?.dataClassification != null ? String(row.dataClassification).trim() : ''))
      .filter(Boolean)
  )
  return kinds.every((k) => {
    const code = k?.genericDataKindCode != null ? String(k.genericDataKindCode).trim() : ''
    return code !== '' && codesWithUpload.has(code)
  })
}

/**
 * データ確定解除モーダル入力の単項目チェック（§8.1・SR-047〜050）
 *
 * @param {{ permitUser?: string | null; permitDate?: string | null }} form
 * @returns {{ ok: boolean; errors: Record<string, string> }} Errors のキーは permitUser / permitDate
 */
export function validateFixCancelForm(form) {
  /** @type {Record<string, string>} */
  const errors = {}
  const permitUser = form?.permitUser != null ? String(form.permitUser).trim() : ''
  const permitDateRaw = form?.permitDate != null ? String(form.permitDate).trim() : ''

  if (permitUser === '') {
    errors.permitUser = getMessage('E010001', '許諾者名')
  }
  if (permitDateRaw === '') {
    errors.permitDate = getMessage('E010001', '許諾日')
  } else if (!_isValidYyyyMmDd(permitDateRaw)) {
    errors.permitDate = MSG_PERMIT_DATE_FORMAT
  }

  return { ok: Object.keys(errors).length === 0, errors }
}

/**
 * データ更新ボタン押下時の事前条件（§7.5.3・SR-051〜054）。先頭から順に評価し最初の NG メッセージのみ返す。
 *
 * @param {{
 *   genericDataKinds: import('./types').GenericDataKindItem[] | null | undefined
 *   uploadFileList: import('./types').UploadFileListItem[] | null | undefined
 *   statusByDataKind: Record<string, string> | null | undefined
 *   dataTaskStatus: string | null | undefined
 *   dataFixStatus: string | null | undefined
 * }} params
 * @returns {{ ok: boolean; message?: string }}
 */
/**
 * Upload_file_list の有効行がすべて uploaded か（pending-deletion は対象外）
 *
 * @param {import('./types').UploadFileListItem[] | null | undefined} uploadFileList
 * @returns {boolean}
 */
export function allUploadFilesAreUploaded(uploadFileList) {
  if (!Array.isArray(uploadFileList)) {
    return false
  }
  let activeCount = 0
  for (const row of uploadFileList) {
    if (row.status === GENERIC_UPLOAD_FILE_STATUS_PENDING_DELETION) {
      continue
    }
    activeCount += 1
    if (row.status !== GENERIC_UPLOAD_FILE_STATUS_UPLOADED) {
      return false
    }
  }
  return activeCount > 0
}

export function validateDataUpdatePreconditions(params) {
  const { statusByDataKind, dataTaskStatus, dataFixStatus } = params ?? {}

  for (const [code, status] of Object.entries(statusByDataKind ?? {})) {
    if (code === GENERIC_DATA_KIND_GENERIC_ITEM) continue
    const s = String(status ?? '')
    if (s === GENERIC_STATUS_VERIFYING || s === GENERIC_STATUS_VERIFICATION_ERROR) {
      return { ok: false, message: MSG_DATA_UPDATE_VERIFYING_OR_ERROR }
    }
  }

  if (String(dataTaskStatus ?? '') === DATA_TASK_STATUS_CREATING) {
    return { ok: false, message: MSG_DATA_UPDATE_CREATING }
  }
  if (String(dataFixStatus ?? '') === DATA_FIX_STATUS_FIX) {
    return { ok: false, message: MSG_DATA_UPDATE_ALREADY_FIXED }
  }
  return { ok: true }
}

/**
 * 検証待ち（verification-pending）のデータ分類が1件以上あるか
 *
 * @param {Record<string, string> | null | undefined} statusByDataKind
 * @returns {boolean}
 */
export function hasPendingValidationKinds(statusByDataKind) {
  for (const [code, s] of Object.entries(statusByDataKind ?? {})) {
    if (code === GENERIC_DATA_KIND_GENERIC_ITEM) {
      continue
    }
    if (String(s) === GENERIC_STATUS_PENDING_VERIFICATION) {
      return true
    }
  }
  return false
}

/**
 * データ検証一括実行前の事前条件（§7.4.3・§8.3・SR-055）。
 *
 * - 親タスクが「作成中（creating）」の場合は API 側（IF02097）が 400 を返すため、UI 側でも先回りでブロックする。
 * - 検証対象が 0 件なら API 呼び出し不要。
 *
 * @param {{
 *   statusByDataKind: Record<string, string> | null | undefined
 *   dataTaskStatus?: string | null | undefined
 * }} params
 * @returns {{ ok: boolean; message?: string }}
 */
export function validateDataValidationBulkPreconditions(params) {
  const { statusByDataKind, dataTaskStatus } = params ?? {}
  if (String(dataTaskStatus ?? '') === DATA_TASK_STATUS_CREATING) {
    return { ok: false, message: MSG_DATA_UPDATE_CREATING }
  }
  if (!hasPendingValidationKinds(statusByDataKind)) {
    return { ok: false, message: MSG_VALIDATION_BULK_NO_PENDING }
  }
  return { ok: true }
}
